import { useEffect } from "react";
import { useDispatch } from "react-redux";
import toast from "react-hot-toast";

import {
  createTournamentPhase,
  bulkCreateTournamentPhases,
  createTournamentBlock,
  deletePhase,
  deleteBlock,
  fetchTournaments,
  getSelectedTournament,
  updatePhase,
  updateBlock,
  assignTeamsToTournamentBlock,
  deleteTeamsFromBlock,
  createProgressionRule,
  updateProgressionRule,
  deleteProgressionRule,
  executeProgressionRules,
  updateTeamExtraPoints,
  selectCategory,
} from "../../../../redux/slices/tournamentSlice";
import { formatCreated } from "../../../../utils/analytics";

const useFormatHandlers = (state) => {
  const dispatch = useDispatch();

  // Destructure needed state values
  const {
    selectedOrganisation,
    selectedTournament,
    selectedCategory: selectedCategoryFromRedux,

    // Core state
    phases,
    setIsLoading,
    selectedCategoryLabel: selectedCategory,

    // Template selection
    setSelectedTemplate,

    // Phase modals
    setIsAddPhaseModalOpen,
    setIsPhaseSettingsModalOpen,
    setIsCreateFormatModalOpen,
    setIsPhaseDeleteModalOpen,
    phaseToDelete,
    setPhaseToDelete,
    editingPhase,
    setEditingPhase,
    setPhaseInitialData,

    // Match state
    setIsMatchModalOpen,
    setIsDeleteMatchModalOpen,
    matchMode,
    setMatchMode,
    selectedPhaseForMatch,
    setSelectedPhaseForMatch,
    selectedMatchGroup,
    setSelectedMatchGroup,
    setMatchFormData,

    // Group state
    setIsGroupModalOpen,
    setIsDeleteGroupModalOpen,
    groupMode,
    setGroupMode,
    selectedPhaseForGroup,
    setSelectedPhaseForGroup,
    selectedGroup,
    setSelectedGroup,
    setGroupFormData,

    // Bracket state
    setIsBracketModalOpen,
    setIsDeleteBracketModalOpen,
    bracketMode,
    setBracketMode,
    selectedPhaseForBracket,
    setSelectedPhaseForBracket,
    selectedBracket,
    setSelectedBracket,
    setBracketFormData,

    // Section state
    setIsBracketSectionModalOpen,
    selectedBracketForSection,
    setSelectedBracketForSection,
    setSectionFormData,

    // Bracket-group state
    setIsBracketGroupModalOpen,
    selectedBracketForGroup,
    setSelectedBracketForGroup,
    setBracketGroupFormData,

    // Bracket-match state
    setIsBracketMatchModalOpen,
    selectedBracketForMatch,
    setSelectedBracketForMatch,
    setBracketMatchFormData,

    // Team assignment
    showTeamSelector,
    setShowTeamSelector,
    setSelectedTeamSlot,
    selectedPhaseId,
    setSelectedPhaseId,
    selectedBlockId,
    setSelectedBlockId,
    teamSelectorRef,

    // Progression rules
    progressionRulesMapping,
    setProgressionRulesMapping,
    setIsCreateRuleModalOpen,
    setIsEditRuleModalOpen,
    setIsExecuteRulesModalOpen,
    selectedPhaseForRule,
    setSelectedPhaseForRule,
    selectedRule,
    setSelectedRule,
    ruleFormData,
    setRuleFormData,
    executeRulesData,
    setExecuteRulesData,

    // Extra points
    setIsExtraPointsModalOpen,
    selectedGroupForExtraPoints,
    setSelectedGroupForExtraPoints,
    extraPointsFormData,
    setExtraPointsFormData,
  } = state;

  // ============================================
  // REFRESH CONTENT
  // ============================================

  const refreshContent = async () => {
    // Capture current category _id before any dispatch can overwrite it
    const currentCategoryId = selectedCategoryFromRedux?._id;
    try {
      await dispatch(
        fetchTournaments({
          organisationId: selectedOrganisation.id,
          page: 1,
          limit: 10,
          search: "",
        })
      ).unwrap();
      // Also fetch the full detailed tournament to get deeply nested data (bracket rounds/fixtures)
      const result = await dispatch(getSelectedTournament(selectedOrganisation.id)).unwrap();

      // Restore the correct category — fetchTournaments may have reset selectedCategory
      // to null or categories[0] if the selected tournament wasn't in the list response
      if (currentCategoryId) {
        const tournament =
          result?.data?.tournament || result?.tournament || result?.data || result;
        const categories = Array.isArray(tournament?.categories) ? tournament.categories : [];
        const matchingCategory = categories.find(
          (cat) => (cat._id || cat.id) === currentCategoryId
        );
        if (matchingCategory) {
          dispatch(selectCategory(matchingCategory));
        }
      }
    } catch (error) {
    }
  };

  // ============================================
  // TEMPLATE SELECT
  // ============================================

  const handleTemplateSelect = (templateId) => {
    // Always go into create mode when selecting a template
    setEditingPhase(null);
    setPhaseInitialData(null);
    setSelectedTemplate(templateId);
    setIsAddPhaseModalOpen(false);

    if (templateId === "manualGrid") {
      // Manual grid → immediately create a phase with defaults
      handleAddPhaseQuick();
    } else {
      // Knockout / Group / Group+Knockout → new single-page modal
      setIsCreateFormatModalOpen(true);
    }
  };

  // ============================================
  // TEAM ASSIGNMENT FUNCTIONS
  // ============================================

  // Get available teams that haven't been assigned to the current group
  const getAvailableTeams = () => {
    if (!selectedCategoryFromRedux?.teams) return [];

    const currentPhase = selectedCategoryFromRedux.phases
      .find((phase) => phase._id === selectedPhaseId);

    if (!currentPhase) return [];

    // Collect all team IDs assigned across ALL blocks in this stage
    const assignedTeamIds = new Set();
    (currentPhase.blocks || []).forEach((block) => {
      (block.teams || []).forEach((team) => {
        if (team.teamId) {
          assignedTeamIds.add(team.teamId._id || team.teamId);
        }
      });
    });

    // Filter out teams already assigned anywhere in this stage, and teams with no resolvable name
    return selectedCategoryFromRedux.teams.filter(
      (team) => !assignedTeamIds.has(team._id) && (team.status === "ACCEPTED" || team.status === "PENDING") && (team.teamId?.title || team.teamTitle)
    );
  };

  // Get team name by ID
  const getTeamById = (id) => {
    if (!id) return null;
    const idStr = id._id ? id._id.toString() : id.toString();
    const selectedTeam = selectedCategoryFromRedux?.teams?.find(
      (team) => team._id.toString() === idStr || team.teamId?._id?.toString() === idStr
    );
    return selectedTeam?.teamId?.title || selectedTeam?.teamTitle || null;
  };

  // Handle assigning a team to a slot
  const handleAssignTeam = async (teamId, dummySlotNumber) => {
    setIsLoading(true);
    const loadingToast = toast.loading("Assigning team...");
    try {
      await dispatch(
        assignTeamsToTournamentBlock({
          tournamentId: selectedTournament.id,
          categoryId: selectedCategoryFromRedux._id,
          phaseId: selectedPhaseId,
          blockId: selectedBlockId,
          teamsData: {
            teams: [{ teamId, dummySlotNumber }],
          },
        })
      ).unwrap();

      toast.dismiss(loadingToast);
      toast.success("Team assigned successfully");
      setShowTeamSelector(false);
      setSelectedTeamSlot(null);
      setSelectedBlockId(null);
      setSelectedPhaseId(null);
      await refreshContent();
    } catch (error) {
      toast.dismiss(loadingToast);
      toast.error(error.message || "Error assigning team");
    } finally {
      setIsLoading(false);
    }
  };

  // Handle unassigning a team from a slot
  const handleUnassignTeam = async (teamId) => {
    setIsLoading(true);
    const loadingToast = toast.loading("Unassigning team...");
    const id = teamId?.id || teamId?._id || (typeof teamId === "string" ? teamId : String(teamId));
    try {
      await dispatch(
        deleteTeamsFromBlock({
          tournamentId: selectedTournament.id,
          categoryId: selectedCategoryFromRedux._id,
          phaseId: selectedPhaseId,
          blockId: selectedBlockId,
          teamIds: [id],
        })
      ).unwrap();

      toast.dismiss(loadingToast);
      toast.success("Team unassigned successfully");
      setShowTeamSelector(false);
      setSelectedTeamSlot(null);
      setSelectedBlockId(null);
      setSelectedPhaseId(null);
      await refreshContent();
    } catch (error) {
      toast.dismiss(loadingToast);
      toast.error(error.message || "Error unassigning team");
    } finally {
      setIsLoading(false);
    }
  };

  // Auto-assign available teams randomly to empty slots in a group block
  const handleAutoAssignTeams = async (phaseId, block) => {
    if (!selectedCategoryFromRedux?.teams) return;

    const tournamentId = selectedTournament?.id || selectedTournament?._id;
    const categoryId = selectedCategoryFromRedux?._id || selectedCategoryFromRedux?.id;
    if (!tournamentId || !categoryId) return;

    // Find the phase to check assignments across all blocks
    const currentPhase = selectedCategoryFromRedux.phases?.find(
      (p) => p._id === phaseId || p.id === phaseId
    );
    if (!currentPhase) return;

    // Collect all team IDs already assigned in ANY block in this stage
    const assignedTeamIds = new Set();
    (currentPhase.blocks || []).forEach((b) => {
      (b.teams || []).forEach((t) => {
        if (t.teamId) {
          assignedTeamIds.add(t.teamId._id || t.teamId);
        }
      });
    });

    // Get available teams (not assigned anywhere in this stage, accepted/pending)
    const availableTeams = selectedCategoryFromRedux.teams.filter(
      (team) => !assignedTeamIds.has(team._id) && (team.status === "ACCEPTED" || team.status === "PENDING")
    );

    // Get empty slots in this block (dummy slots without a real team assigned).
    // For brackets we only target first-round (seed) slots; progression slots
    // (dummySlotNumber > maxTeams) get filled by future round results.
    const isBracket = block.type === "BRACKET";
    const seedCap = isBracket ? (block.maxTeams || Infinity) : Infinity;
    const emptySlots = (block.teams || []).filter(
      (team) => !team.teamId && team.dummySlotNumber <= seedCap
    );

    if (emptySlots.length === 0) {
      toast(
        isBracket
          ? "All first-round slots are already assigned"
          : "All slots in this group are already assigned",
        { icon: "ℹ️" }
      );
      return;
    }

    if (availableTeams.length === 0) {
      toast("No available teams to assign in this stage", { icon: "ℹ️" });
      return;
    }

    // Shuffle available teams randomly
    const shuffled = [...availableTeams].sort(() => Math.random() - 0.5);

    // Take as many as there are empty slots (or fewer if not enough teams)
    const teamsToAssign = shuffled.slice(0, emptySlots.length);

    // Build bulk assignment payload
    const teams = teamsToAssign.map((team, index) => ({
      teamId: team._id,
      dummySlotNumber: emptySlots[index].dummySlotNumber,
    }));

    setIsLoading(true);
    const loadingToast = toast.loading("Auto-assigning teams...");
    try {
      await dispatch(
        assignTeamsToTournamentBlock({
          tournamentId,
          categoryId,
          phaseId,
          blockId: block._id || block.id,
          teamsData: { teams },
        })
      ).unwrap();

      toast.dismiss(loadingToast);
      toast.success(`${teams.length} team${teams.length > 1 ? "s" : ""} assigned`);
      await refreshContent();
    } catch (error) {
      toast.dismiss(loadingToast);
      toast.error(error.message || "Error auto-assigning teams");
    } finally {
      setIsLoading(false);
    }
  };

  // Auto-assign available teams round-robin across ALL group blocks in a phase
  const handleAutoAssignAllGroups = async (phaseId) => {
    if (!selectedCategoryFromRedux?.teams) return;

    const tournamentId = selectedTournament?.id || selectedTournament?._id;
    const categoryId = selectedCategoryFromRedux?._id || selectedCategoryFromRedux?.id;
    if (!tournamentId || !categoryId) return;

    const currentPhase = selectedCategoryFromRedux.phases?.find(
      (p) => p._id === phaseId || p.id === phaseId
    );
    if (!currentPhase) return;

    const groupBlocks = (currentPhase.blocks || []).filter(
      (b) => b.type === "GROUP"
    );
    if (groupBlocks.length === 0) return;

    // Collect all team IDs already assigned in ANY block in this stage
    const assignedTeamIds = new Set();
    (currentPhase.blocks || []).forEach((b) => {
      (b.teams || []).forEach((t) => {
        if (t.teamId) {
          assignedTeamIds.add(t.teamId._id || t.teamId);
        }
      });
    });

    // Get available teams (not assigned anywhere in this stage, accepted/pending)
    const availableTeams = selectedCategoryFromRedux.teams.filter(
      (team) =>
        !assignedTeamIds.has(team._id) &&
        (team.status === "ACCEPTED" || team.status === "PENDING")
    );

    if (availableTeams.length === 0) {
      toast("No available teams to assign in this stage", { icon: "ℹ️" });
      return;
    }

    // Build empty slots per group block
    const groupSlots = groupBlocks.map((block) => ({
      block,
      emptySlots: (block.teams || []).filter((t) => !t.teamId),
    }));

    const totalEmptySlots = groupSlots.reduce(
      (sum, g) => sum + g.emptySlots.length,
      0
    );

    if (totalEmptySlots === 0) {
      toast("All slots in all groups are already assigned", { icon: "ℹ️" });
      return;
    }

    // Shuffle available teams randomly
    const shuffled = [...availableTeams].sort(() => Math.random() - 0.5);

    // Distribute teams round-robin across groups
    const assignments = groupSlots.map(() => []); // one array per group
    let teamIndex = 0;
    let slotsFilled = true;

    while (teamIndex < shuffled.length && slotsFilled) {
      slotsFilled = false;
      for (let g = 0; g < groupSlots.length && teamIndex < shuffled.length; g++) {
        const { emptySlots } = groupSlots[g];
        if (assignments[g].length < emptySlots.length) {
          assignments[g].push({
            teamId: shuffled[teamIndex]._id,
            dummySlotNumber: emptySlots[assignments[g].length].dummySlotNumber,
          });
          teamIndex++;
          slotsFilled = true;
        }
      }
    }

    setIsLoading(true);
    const loadingToast = toast.loading("Auto-assigning teams to all groups...");
    try {
      for (let g = 0; g < groupSlots.length; g++) {
        if (assignments[g].length === 0) continue;
        await dispatch(
          assignTeamsToTournamentBlock({
            tournamentId,
            categoryId,
            phaseId,
            blockId: groupSlots[g].block._id || groupSlots[g].block.id,
            teamsData: { teams: assignments[g] },
          })
        ).unwrap();
      }

      const totalAssigned = assignments.reduce((sum, a) => sum + a.length, 0);
      toast.dismiss(loadingToast);
      toast.success(
        `${totalAssigned} team${totalAssigned > 1 ? "s" : ""} assigned across ${groupSlots.length} group${groupSlots.length > 1 ? "s" : ""}`
      );
      await refreshContent();
    } catch (error) {
      toast.dismiss(loadingToast);
      toast.error(error.message || "Error auto-assigning teams");
    } finally {
      setIsLoading(false);
    }
  };

  // Open team selector for a specific slot
  const openTeamSelector = (phaseId, blockId, slotIndex) => {
    setSelectedPhaseId(phaseId);
    setSelectedBlockId(blockId);
    setSelectedTeamSlot(slotIndex);
    setShowTeamSelector(true);
  };

  // Close team selector
  const closeTeamSelector = () => {
    setShowTeamSelector(false);
    setSelectedTeamSlot(null);
    setSelectedBlockId(null);
    setSelectedPhaseId(null);
  };

  // Click outside handler for team selector
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (teamSelectorRef.current && !teamSelectorRef.current.contains(event.target)) {
        closeTeamSelector();
      }
    };

    if (showTeamSelector) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showTeamSelector]);

  // ============================================
  // PROGRESSION RULES FUNCTIONS
  // ============================================

  // Build progression rules mapping for display
  useEffect(() => {
    if (!selectedCategoryFromRedux?.phases) return;

    const allRules = [];
    selectedCategoryFromRedux.phases.forEach((phase) => {
      if (phase.rules && Array.isArray(phase.rules)) {
        allRules.push(...phase.rules);
      }
    });

    // Build group names map and block fixture lists
    const groupNamesMap = {};
    const blockFixturesMap = {};
    selectedCategoryFromRedux.phases.forEach((phase) => {
      phase.blocks.forEach((block) => {
        groupNamesMap[block._id] = block.name;
        // Build flat fixture list for bracket blocks
        if (block.type === "BRACKET" && block.rounds) {
          const flatFixtures = [];
          block.rounds.forEach((round) => {
            if (round.fixtures) {
              // tieNumber is the bracket-wide match number (QF-1=1, ..., SF-1=5,
              // SF-2=6, Final=7), so we collapse legs to a position within the
              // round here: 1st unique tieNumber seen → 1, 2nd → 2, etc.
              const tiePositionInRound = new Map();
              round.fixtures.forEach((fixture, fixtureIdx) => {
                let matchNumberInRound;
                if (fixture.tieNumber != null) {
                  if (!tiePositionInRound.has(fixture.tieNumber)) {
                    tiePositionInRound.set(fixture.tieNumber, tiePositionInRound.size + 1);
                  }
                  matchNumberInRound = tiePositionInRound.get(fixture.tieNumber);
                } else {
                  matchNumberInRound = fixtureIdx + 1;
                }
                flatFixtures.push({
                  name: fixture.name,
                  roundName: round.name,
                  matchNumberInRound,
                });
              });
            }
          });
          blockFixturesMap[block._id] = flatFixtures;
        }
      });
    });

    // Build the mapping object for progression rules display
    const rulesMapping = {};

    // 1. Derive bracket-internal progression text (Round 2+ slots ← winners of previous round)
    selectedCategoryFromRedux.phases.forEach((phase) => {
      phase.blocks.forEach((block) => {
        if (block.type !== "BRACKET" || !block.rounds) return;

        // Build a map: slot number → { roundName, matchLabel } for each fixture's home/away slot
        // so we can look up "which fixture produced this slot"
        const slotToFixtureMap = {};
        block.rounds.forEach((round, roundIdx) => {
          if (!round.fixtures) return;
          // Group fixtures by tieNumber to get unique matches (ignore extra legs)
          const seen = new Set();
          let matchInRound = 0;
          round.fixtures.forEach((fixture) => {
            const tie = fixture.tieNumber ?? fixture.matchNumber;
            if (seen.has(tie)) return;
            seen.add(tie);
            matchInRound++;

            // For Round 2+, the home/away slots are progression slots fed by previous-round winners
            if (roundIdx > 0) {
              const matchLabel = `${round.name || `Round ${roundIdx + 1}`} M${matchInRound}`;
              slotToFixtureMap[fixture.homeTeamDummySlot] = { roundName: round.name, matchInRound, matchLabel, roundIdx };
              slotToFixtureMap[fixture.awayTeamDummySlot] = { roundName: round.name, matchInRound, matchLabel, roundIdx };
            }
          });
        });

        // Now map: for each progression slot, find the previous-round fixture whose winner fills it
        // A progression slot X in Round N is filled by the winner of a match in Round N-1
        // The match in Round N-1 that feeds slot X is the one where slot X appears as the output
        // We find it by looking at which Round N-1 fixture's match position corresponds
        block.rounds.forEach((round, roundIdx) => {
          if (roundIdx === 0 || !round.fixtures) return;

          const prevRound = block.rounds[roundIdx - 1];
          if (!prevRound?.fixtures) return;

          // Get unique matches from previous round
          const prevMatches = [];
          const prevSeen = new Set();
          prevRound.fixtures.forEach((f) => {
            const tie = f.tieNumber ?? f.matchNumber;
            if (prevSeen.has(tie)) return;
            prevSeen.add(tie);
            prevMatches.push(f);
          });

          // Get unique matches from current round
          const currSeen = new Set();
          let currMatchIdx = 0;
          round.fixtures.forEach((fixture) => {
            const tie = fixture.tieNumber ?? fixture.matchNumber;
            if (currSeen.has(tie)) return;
            currSeen.add(tie);

            // Each current-round match is fed by 2 consecutive previous-round matches
            const prevMatch1 = prevMatches[currMatchIdx * 2];
            const prevMatch2 = prevMatches[currMatchIdx * 2 + 1];

            if (!rulesMapping[block._id]) rulesMapping[block._id] = {};

            // Get previous round display info
            const prevRoundName = prevRound.name || `Round ${roundIdx}`;
            const prevSeen2 = new Set();
            let prevMatchNum1 = 0, prevMatchNum2 = 0;
            let counter = 0;
            prevRound.fixtures.forEach((f) => {
              const t = f.tieNumber ?? f.matchNumber;
              if (prevSeen2.has(t)) return;
              prevSeen2.add(t);
              counter++;
              if (f === prevMatch1) prevMatchNum1 = counter;
              if (f === prevMatch2) prevMatchNum2 = counter;
            });

            // Generate short round prefix
            const getShortRoundName = (name) => {
              const rn = (name || "").toLowerCase();
              if (rn.includes("quarter")) return "QF";
              if (rn.includes("semi")) return "SF";
              if (rn === "final") return "Final";
              if (rn.includes("round of")) return `R${rn.replace(/\D/g, "")}`;
              return name;
            };
            const shortPrev = getShortRoundName(prevRoundName);

            if (prevMatch1) {
              rulesMapping[block._id][fixture.homeTeamDummySlot] = `Winner - ${shortPrev}-${prevMatchNum1}`;
            }
            if (prevMatch2) {
              rulesMapping[block._id][fixture.awayTeamDummySlot] = `Winner - ${shortPrev}-${prevMatchNum2}`;
            }

            currMatchIdx++;
          });
        });
      });
    });

    // 2. Overlay explicit progression rules (these take precedence)
    const ordSuffix = (n) => {
      const s = ["th", "st", "nd", "rd"];
      const v = n % 100;
      return n + (s[(v - 20) % 10] || s[v] || s[0]);
    };

    allRules.forEach((rule) => {
      const { targetBlockId, targetTeamSlot, sourceBlockId, sourcePosition, sourceType, sourceFixtureIndex, sourceOutcome, sourceRankAmongstSources } = rule;
      const sourceGroupName = groupNamesMap[sourceBlockId] || "Unknown Group";

      if (!rulesMapping[targetBlockId]) {
        rulesMapping[targetBlockId] = {};
      }

      if (sourceType === "FIXTURE_RESULT" && sourceFixtureIndex != null) {
        const fixtures = blockFixturesMap[sourceBlockId];
        const sourceFixture = fixtures?.[sourceFixtureIndex];
        const outcome = sourceOutcome === "LOSER" ? "Loser" : "Winner";
        const roundName = sourceFixture?.roundName || "Round";
        const rn = (roundName || "").toLowerCase();
        const shortRound = rn.includes("quarter") ? "QF" : rn.includes("semi") ? "SF" : rn === "final" ? "Final" : rn.includes("round of") ? `R${rn.replace(/\D/g, "")}` : roundName;
        const matchNum = sourceFixture?.matchNumberInRound || (sourceFixtureIndex + 1);
        rulesMapping[targetBlockId][targetTeamSlot] = `${outcome} - ${shortRound}-${matchNum}`;
      } else if (sourceType === "BEST_AT_POSITION") {
        // Cross-group ranking: "3rd-place (1)" best, "3rd-place (2)" 2nd-best, etc.
        // Position drives the place label (2nd/3rd/4th); rank is the parenthetical.
        const rank = sourceRankAmongstSources || 1;
        const placeLabel = sourcePosition != null ? `${ordSuffix(sourcePosition)}-place` : "Best";
        rulesMapping[targetBlockId][targetTeamSlot] = `${placeLabel} (${rank})`;
      } else if (sourcePosition === 1) {
        rulesMapping[targetBlockId][targetTeamSlot] = `Winner - ${sourceGroupName}`;
      } else if (sourcePosition === 2) {
        rulesMapping[targetBlockId][targetTeamSlot] = `Runner-up - ${sourceGroupName}`;
      } else if (sourcePosition != null) {
        rulesMapping[targetBlockId][targetTeamSlot] = `${sourceGroupName} P${sourcePosition}`;
      } else {
        rulesMapping[targetBlockId][targetTeamSlot] = `${sourceGroupName}`;
      }
    });

    setProgressionRulesMapping(rulesMapping);
  }, [selectedCategoryFromRedux, setProgressionRulesMapping]);

  // Create a new progression rule
  const handleCreateRule = async () => {
    if (!selectedPhaseForRule) {
      toast.error("No stage selected for rule creation");
      return;
    }

    setIsLoading(true);
    const loadingToast = toast.loading("Creating progression rule...");
    try {
      await dispatch(
        createProgressionRule({
          tournamentId: selectedTournament.id,
          categoryId: selectedCategoryFromRedux._id,
          phaseId: selectedPhaseForRule._id || selectedPhaseForRule.id,
          ruleData: ruleFormData,
        })
      ).unwrap();

      await refreshContent();
      toast.dismiss(loadingToast);
      toast.success("Progression rule created successfully");
      setIsCreateRuleModalOpen(false);
      setRuleFormData({
        sourceBlockId: "",
        targetBlockId: "",
        targetPhaseId: "",
        sourcePosition: 1,
        targetTeamSlot: 1,
      });
    } catch (error) {
      toast.dismiss(loadingToast);
      toast.error(error.message || "Error creating progression rule");
    } finally {
      setIsLoading(false);
    }
  };

  // Update an existing progression rule
  const handleUpdateRule = async () => {
    if (!selectedPhaseForRule || !selectedRule) {
      toast.error("No rule selected for update");
      return;
    }

    setIsLoading(true);
    const loadingToast = toast.loading("Updating progression rule...");
    try {
      await dispatch(
        updateProgressionRule({
          tournamentId: selectedTournament.id,
          categoryId: selectedCategoryFromRedux._id,
          phaseId: selectedPhaseForRule._id || selectedPhaseForRule.id,
          ruleId: selectedRule._id,
          ruleData: ruleFormData,
        })
      ).unwrap();

      await refreshContent();
      toast.dismiss(loadingToast);
      toast.success("Progression rule updated successfully");
      setIsEditRuleModalOpen(false);
      setSelectedRule(null);
      setRuleFormData({
        sourceBlockId: "",
        targetBlockId: "",
        targetPhaseId: "",
        sourcePosition: 1,
        targetTeamSlot: 1,
      });
    } catch (error) {
      toast.dismiss(loadingToast);
      toast.error(error.message || "Error updating progression rule");
    } finally {
      setIsLoading(false);
    }
  };

  // Delete a progression rule
  const handleDeleteRule = async (ruleId) => {
    if (!selectedPhaseForRule) {
      toast.error("No stage selected");
      return;
    }

    if (!window.confirm("Are you sure you want to delete this rule?")) {
      return;
    }

    setIsLoading(true);
    const loadingToast = toast.loading("Deleting rule...");
    try {
      await dispatch(
        deleteProgressionRule({
          tournamentId: selectedTournament.id,
          categoryId: selectedCategoryFromRedux._id,
          phaseId: selectedPhaseForRule._id || selectedPhaseForRule.id,
          ruleId,
        })
      ).unwrap();

      await refreshContent();
      toast.dismiss(loadingToast);
      toast.success("Rule deleted successfully");
    } catch (error) {
      toast.dismiss(loadingToast);
      toast.error(error.message || "Error deleting rule");
    } finally {
      setIsLoading(false);
    }
  };

  // Execute progression rules
  const handleExecuteRules = async (phaseId) => {
    setIsLoading(true);
    const loadingToast = toast.loading("Executing progression rules...");
    try {
      // Find the phase and its rules for debugging
      const targetPhase = selectedCategoryFromRedux?.phases?.find(p => p._id === phaseId);

      const result = await dispatch(
        executeProgressionRules({
          tournamentId: selectedTournament.id,
          categoryId: selectedCategoryFromRedux._id,
          phaseId,
          body: {
            category: executeRulesData.category || undefined,
            blockId: executeRulesData.blockId || undefined,
          },
        })
      ).unwrap();


      await refreshContent();


      toast.dismiss(loadingToast);
      toast.success("Progression rules executed successfully");
      setIsExecuteRulesModalOpen(false);
      setExecuteRulesData({ category: "", blockId: "" });
    } catch (error) {
      toast.dismiss(loadingToast);
      toast.error(error.message || error || "Error executing progression rules");
    } finally {
      setIsLoading(false);
    }
  };

  // Open edit rule modal with existing data
  const openEditRule = (phase, rule) => {
    setSelectedPhaseForRule(phase);
    setSelectedRule(rule);
    setRuleFormData({
      sourceBlockId: rule.sourceBlockId,
      targetBlockId: rule.targetBlockId,
      targetPhaseId: rule.targetPhaseId,
      sourcePosition: rule.sourcePosition,
      targetTeamSlot: rule.targetTeamSlot,
    });
    setIsEditRuleModalOpen(true);
  };

  // Get progression rule display text for a team slot
  const getProgressionRuleText = (blockId, slotNumber) => {
    if (progressionRulesMapping[blockId] && progressionRulesMapping[blockId][slotNumber]) {
      return progressionRulesMapping[blockId][slotNumber];
    }
    return null;
  };

  // Find a progression rule targeting a specific block/slot
  const findRuleForSlot = (blockId, slotNumber) => {
    for (const phase of (selectedCategoryFromRedux?.phases || [])) {
      for (const rule of (phase.rules || [])) {
        if (rule.targetBlockId === blockId && rule.targetTeamSlot === slotNumber) {
          return { rule, phaseId: phase._id || phase.id };
        }
      }
    }
    return null;
  };

  // Remove a progression rule from a slot
  const handleRemoveRule = async (blockId, slotNumber) => {
    const found = findRuleForSlot(blockId, slotNumber);
    if (!found) {
      toast.error("No rule found for this slot");
      return;
    }

    setIsLoading(true);
    const loadingToast = toast.loading("Removing progression rule...");
    try {
      await dispatch(
        deleteProgressionRule({
          tournamentId: selectedTournament.id,
          categoryId: selectedCategoryFromRedux._id,
          phaseId: found.phaseId,
          ruleId: found.rule._id,
        })
      ).unwrap();

      await refreshContent();
      toast.dismiss(loadingToast);
      toast.success("Progression rule removed");
      setShowTeamSelector(false);
    } catch (error) {
      toast.dismiss(loadingToast);
      toast.error(error.message || "Error removing rule");
    } finally {
      setIsLoading(false);
    }
  };

  // Build list of available rule sources for a given target slot
  const getRuleSources = (targetPhaseId, targetBlockId) => {
    const phases = selectedCategoryFromRedux?.phases || [];
    const sources = [];

    // Collect all existing rules to filter out already-used sources
    const allRules = [];
    phases.forEach((phase) => {
      if (phase.rules && Array.isArray(phase.rules)) {
        allRules.push(...phase.rules);
      }
    });

    for (const phase of phases) {
      const phaseId = phase._id || phase.id;

      for (const block of phase.blocks || []) {
        const blockId = block._id || block.id;
        // Can't assign from same block
        if (blockId === targetBlockId) continue;

        if (block.type === "GROUP") {
          // Position sources: 1..N based on non-dummy team count
          const teamCount = (block.teams || []).filter((t) => !t.isDummy).length;
          const maxPositions = Math.max(teamCount, block.maxTeams || 0);
          for (let pos = 1; pos <= maxPositions; pos++) {
            sources.push({
              label: `Position ${pos}`,
              groupLabel: `${phase.name} — ${block.name}`,
              sourceBlockId: blockId,
              sourcePhaseId: phaseId,
              sourceType: "POSITION",
              sourcePosition: pos,
            });
          }
        } else if (block.type === "BRACKET") {
          // Match result sources: winner/loser of each fixture in each round
          (block.rounds || []).forEach((round) => {
            const seen = new Set();
            let matchNum = 0;
            (round.fixtures || []).forEach((fixture) => {
              const tie = fixture.tieNumber ?? fixture.matchNumber;
              if (seen.has(tie)) return;
              seen.add(tie);
              matchNum++;

              const roundLabel = round.name || "Round";
              const matchLabel = `${roundLabel} Match ${matchNum}`;

              sources.push({
                label: `Winner of ${matchLabel}`,
                groupLabel: `${phase.name} — ${block.name}`,
                sourceBlockId: blockId,
                sourcePhaseId: phaseId,
                sourceType: "FIXTURE_RESULT",
                sourceSlotNumber: fixture.homeTeamDummySlot,
                sourceOutcome: "WINNER",
              });
              sources.push({
                label: `Loser of ${matchLabel}`,
                groupLabel: `${phase.name} — ${block.name}`,
                sourceBlockId: blockId,
                sourcePhaseId: phaseId,
                sourceType: "FIXTURE_RESULT",
                sourceSlotNumber: fixture.homeTeamDummySlot,
                sourceOutcome: "LOSER",
              });
            });
          });
        } else if (block.type === "SINGLE_MATCH") {
          // Winner/loser of the single match
          // Use fixture's homeTeamDummySlot if fixtures exist, otherwise fall back to first team's slot
          const fixtures = block.fixtures || [];
          const teams = block.teams || [];
          const slotNumber = fixtures.length > 0
            ? fixtures[0].homeTeamDummySlot
            : (teams[0]?.dummySlotNumber || 1);

          if (teams.length >= 2 || fixtures.length > 0) {
            sources.push({
              label: `Winner`,
              groupLabel: `${phase.name} — ${block.name}`,
              sourceBlockId: blockId,
              sourcePhaseId: phaseId,
              sourceType: "FIXTURE_RESULT",
              sourceSlotNumber: slotNumber,
              sourceOutcome: "WINNER",
            });
            sources.push({
              label: `Loser`,
              groupLabel: `${phase.name} — ${block.name}`,
              sourceBlockId: blockId,
              sourcePhaseId: phaseId,
              sourceType: "FIXTURE_RESULT",
              sourceSlotNumber: slotNumber,
              sourceOutcome: "LOSER",
            });
          }
        }
      }
    }

    // Filter out sources already used in an existing progression rule
    return sources.filter((source) => {
      return !allRules.some((rule) => {
        if (rule.sourceBlockId?.toString() !== source.sourceBlockId?.toString()) return false;
        if (source.sourceType === "POSITION") {
          return rule.sourceType === "POSITION" && rule.sourcePosition === source.sourcePosition;
        }
        if (source.sourceType === "FIXTURE_RESULT") {
          return rule.sourceType === "FIXTURE_RESULT" &&
            rule.sourceSlotNumber === source.sourceSlotNumber &&
            rule.sourceOutcome === source.sourceOutcome;
        }
        return false;
      });
    });
  };

  // Handle rule assignment from click-to-assign dropdown
  const handleAssignRuleFromClick = async (source, targetPhaseId, targetBlockId, targetSlot) => {
    const ruleData = {
      sourceBlockId: source.sourceBlockId,
      targetBlockId,
      targetTeamSlot: targetSlot,
      targetPhaseId,
    };

    if (source.sourceType === "FIXTURE_RESULT") {
      ruleData.sourceType = "FIXTURE_RESULT";
      ruleData.sourceSlotNumber = source.sourceSlotNumber;
      ruleData.sourceOutcome = source.sourceOutcome;
    } else {
      ruleData.sourceType = "POSITION";
      ruleData.sourcePosition = source.sourcePosition;
    }

    // If target slot already has a rule, delete it first (overwrite)
    const allRules = [];
    selectedCategoryFromRedux?.phases?.forEach((phase) => {
      if (phase.rules && Array.isArray(phase.rules)) {
        allRules.push(...phase.rules);
      }
    });
    const existing = allRules.find(
      (r) => r.targetBlockId === targetBlockId && r.targetTeamSlot === targetSlot
    );
    if (existing) {
      try {
        for (const phase of (selectedCategoryFromRedux?.phases || [])) {
          const match = (phase.rules || []).find((r) => r._id === existing._id);
          if (match) {
            await dispatch(
              deleteProgressionRule({
                tournamentId: selectedTournament.id,
                categoryId: selectedCategoryFromRedux._id,
                phaseId: phase._id || phase.id,
                ruleId: existing._id,
              })
            ).unwrap();
            break;
          }
        }
      } catch (error) {
        toast.error("Failed to replace existing rule");
        return;
      }
    }

    setIsLoading(true);
    const loadingToast = toast.loading("Creating progression rule...");

    try {
      await dispatch(
        createProgressionRule({
          tournamentId: selectedTournament.id,
          categoryId: selectedCategoryFromRedux._id,
          phaseId: source.sourcePhaseId,
          ruleData,
        })
      ).unwrap();

      await refreshContent();
      toast.dismiss(loadingToast);
      toast.success("Progression rule created");
      closeTeamSelector();
    } catch (error) {
      toast.dismiss(loadingToast);
      toast.error(error.message || "Error creating progression rule");
    } finally {
      setIsLoading(false);
    }
  };

  // Handle creating a progression rule from drag and drop
  const handleCreateRuleFromDrag = async (sourceItem, targetItem) => {
    if (!sourceItem?.sourceBlockId || !targetItem?.targetBlockId) {
      toast.error("Invalid drag and drop operation");
      return;
    }

    // Get all existing rules
    const allRules = [];
    selectedCategoryFromRedux?.phases?.forEach((phase) => {
      if (phase.rules && Array.isArray(phase.rules)) {
        allRules.push(...phase.rules);
      }
    });

    // Check if source position is already assigned in any rule
    const sourceGroupRules = allRules.filter(
      (rule) =>
        rule.sourceBlockId === sourceItem.sourceBlockId &&
        rule.sourcePosition === sourceItem.sourcePosition
    );

    // TODO: Re-enable one-to-one source position constraint
    // if (sourceGroupRules.length > 0) {
    //   toast.error(
    //     `Position ${sourceItem.sourcePosition} in source group is already assigned to a progression rule`
    //   );
    //   return;
    // }

    // If target slot already has a rule, delete it first (overwrite)
    const existingTargetRule = allRules.find(
      (rule) =>
        rule.targetBlockId === targetItem.targetBlockId &&
        rule.targetTeamSlot === targetItem.targetSlot
    );

    if (existingTargetRule) {
      try {
        // Find which phase owns this rule
        for (const phase of (selectedCategoryFromRedux?.phases || [])) {
          const match = (phase.rules || []).find((r) => r._id === existingTargetRule._id);
          if (match) {
            await dispatch(
              deleteProgressionRule({
                tournamentId: selectedTournament.id,
                categoryId: selectedCategoryFromRedux._id,
                phaseId: phase._id || phase.id,
                ruleId: existingTargetRule._id,
              })
            ).unwrap();
            break;
          }
        }
      } catch (error) {
        toast.error("Failed to replace existing rule");
        return;
      }
    }

    const ruleData = {
      sourceBlockId: sourceItem.sourceBlockId,
      targetBlockId: targetItem.targetBlockId,
      sourcePosition: sourceItem.sourcePosition,
      targetTeamSlot: targetItem.targetSlot,
      targetPhaseId: targetItem.targetPhaseId,
    };

    setIsLoading(true);
    const loadingToast = toast.loading("Creating progression rule...");

    try {
      await dispatch(
        createProgressionRule({
          tournamentId: selectedTournament.id,
          categoryId: selectedCategoryFromRedux._id,
          phaseId: sourceItem.sourcePhaseId,
          ruleData,
        })
      ).unwrap();

      await refreshContent();
      toast.dismiss(loadingToast);
      toast.success("Progression rule created successfully");
    } catch (error) {
      toast.dismiss(loadingToast);
      toast.error(error.message || "Error creating progression rule");
    } finally {
      setIsLoading(false);
    }
  };

  // ============================================
  // EXTRA POINTS FUNCTIONS
  // ============================================

  // Open extra points modal for a group
  const openExtraPointsModal = (phaseId, group) => {
    setSelectedPhaseId(phaseId);
    setSelectedGroupForExtraPoints(group);
    setExtraPointsFormData({
      teamId: null,
      extraPoints: 0,
    });
    setIsExtraPointsModalOpen(true);
  };

  // Handle updating extra points for a team
  const handleUpdateExtraPoints = async () => {
    if (!extraPointsFormData.teamId) {
      toast.error("Please select a team");
      return;
    }

    setIsLoading(true);
    const loadingToast = toast.loading("Updating extra points...");
    try {
      await dispatch(
        updateTeamExtraPoints({
          tournamentId: selectedTournament.id,
          categoryId: selectedCategoryFromRedux._id,
          phaseId: selectedPhaseId,
          blockId: selectedGroupForExtraPoints._id,
          teamId: extraPointsFormData.teamId,
          extraPoints: parseInt(extraPointsFormData.extraPoints) || 0,
        })
      ).unwrap();

      await refreshContent();
      toast.dismiss(loadingToast);
      toast.success("Extra points updated successfully");
      setIsExtraPointsModalOpen(false);
      setExtraPointsFormData({
        teamId: null,
        extraPoints: 0,
      });
    } catch (error) {
      toast.dismiss(loadingToast);
      toast.error(error.message || "Error updating extra points");
    } finally {
      setIsLoading(false);
    }
  };

  // ============================================
  // PHASE HANDLERS
  // ============================================

  // Helper: sanitize phase form data into API-ready phaseData
  const buildPhaseData = (formData) => {
    // Get matchRules (either inherited or from form)
    let matchRules = formData.inheritMatchRules && phases.length > 0
      ? phases[phases.length - 1].matchRules
      : formData.matchRules;

    // Sanitize matchRules: Remove pointsForForfeit and convert strings to numbers
    const { pointsForForfeit, ...restMatchRules } = matchRules || {};
    const sanitizedMatchRules = {
      gameDuration: parseInt(restMatchRules.gameDuration) || 30,
      breaks: parseInt(restMatchRules.breaks) || 1,
      breakDuration: parseInt(restMatchRules.breakDuration) || 5,
      intervalBetweenMatches: parseInt(restMatchRules.intervalBetweenMatches) || 5,
      pointsForWin: parseInt(restMatchRules.pointsForWin) || 0,
      pointsForDraw: parseInt(restMatchRules.pointsForDraw) || 0,
      pointsForLoss: parseInt(restMatchRules.pointsForLoss) || 0,
    };

    // Get matchSchedule with proper defaults for required fields
    const baseSchedule = formData.inheritMatchSchedule && phases.length > 0
      ? phases[phases.length - 1].matchSchedule
      : formData.matchSchedule;

    const today = new Date();
    const defaultStartDate = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    const sixMonthsLater = new Date(today);
    sixMonthsLater.setMonth(sixMonthsLater.getMonth() + 6);
    const defaultEndDate = `${sixMonthsLater.getFullYear()}-${String(sixMonthsLater.getMonth() + 1).padStart(2, '0')}-${String(sixMonthsLater.getDate()).padStart(2, '0')}`;

    const matchSchedule = {
      startDate: baseSchedule?.startDate || defaultStartDate,
      endDate: baseSchedule?.endDate || defaultEndDate,
      matchDays: baseSchedule?.matchDays || ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
      selectedDates: [],
      matchTimings: baseSchedule?.matchTimings || [],
      startTime: formData.useSingleStartTime
        ? (baseSchedule?.matchTimings?.[0] || "09:00")
        : (baseSchedule?.startTime || ""),
      dayEndTime: baseSchedule?.dayEndTime || "21:00",
    };

    return {
      name: formData.name.trim(),
      followMatchTimings: !formData.useSingleStartTime,
      availablePitches: formData.availablePitches || [],
      startAfterPreviousPhase: formData.startAfterPreviousPhase || false,
      matchRules: sanitizedMatchRules,
      matchSchedule,
    };
  };

  const handleSubmitPhase = async (formData, extraConfig) => {
    // Validation (extra safety in parent)
    if (!formData?.name || !formData.name.trim()) {
      toast.error("Please enter a stage name");
      return;
    }

    const tournamentId = selectedTournament?.id || selectedTournament?._id;
    if (!tournamentId) {
      toast.error("Please select a tournament first");
      return;
    }

    setIsLoading(true);
    try {
      const categoryId = selectedCategoryFromRedux?._id || selectedCategoryFromRedux?.id;

      if (!categoryId) {
        toast.error("Please select a category first");
        return;
      }

      const templateType = extraConfig?.templateType || "manualGrid";

      if (editingPhase) {
        // EDIT MODE — always single phase update
        const phaseData = buildPhaseData(formData);
        await dispatch(
          updatePhase({
            tournamentId,
            categoryId,
            phaseId: editingPhase._id || editingPhase.id,
            phaseData,
          })
        ).unwrap();

        toast.success("Stage updated successfully!");
      } else if (templateType === "manualGrid") {
        // MANUAL CREATE — single phase, no blocks
        const phaseData = buildPhaseData(formData);
        await dispatch(
          createTournamentPhase({
            tournamentId,
            categoryId,
            phaseData,
          })
        ).unwrap();

        formatCreated({
          templateType: "manualGrid",
          competitionId: tournamentId,
          categoryId,
          phaseCount: 1,
        });

        toast.success("Stage created successfully!");
      } else {
        // TEMPLATE CREATE — bulk create phase(s) + blocks
        const phaseData = buildPhaseData(formData);
        const { groupConfig, bracketConfig, phase2Settings } = extraConfig;
        const phasesPayload = [];

        // Resolve bracket team count (handles "custom" option)
        const resolveBracketTeams = () => {
          if (bracketConfig.teamsCount === "custom") {
            return parseInt(bracketConfig.customTeamsCount) || 8;
          }
          return parseInt(bracketConfig.teamsCount) || 8;
        };

        if (templateType === "knockoutPhase") {
          phasesPayload.push({
            ...phaseData,
            blocks: [{
              name: bracketConfig.bracketName || "Main Bracket",
              type: "BRACKET",
              maxTeams: resolveBracketTeams(),
              numberOfLegs: parseInt(bracketConfig.numberOfLegs) || 1,
            }],
          });
        } else if (templateType === "groupPhase") {
          const blocks = [];
          const numGroups = parseInt(groupConfig.numGroups) || 2;
          for (let i = 1; i <= numGroups; i++) {
            blocks.push({
              name: `${groupConfig.groupNamePrefix} ${i}`,
              type: "GROUP",
              maxTeams: parseInt(groupConfig.teamsPerGroup) || 4,
              timesToFaceEachOther: parseInt(groupConfig.timesToFaceEachOther) || 1,
              fixtureMode: groupConfig.fixtureMode || "ROUND_ROBIN",
              matchesPerTeam: groupConfig.fixtureMode === "FIXED_MATCHES" ? (parseInt(groupConfig.matchesPerTeam) || 1) : null,
            });
          }
          phasesPayload.push({ ...phaseData, blocks });
        } else if (templateType === "groupStageKnockout") {
          // Phase 1: Group stage
          const groupBlocks = [];
          const numGroups = parseInt(groupConfig.numGroups) || 2;
          for (let i = 1; i <= numGroups; i++) {
            groupBlocks.push({
              name: `${groupConfig.groupNamePrefix} ${i}`,
              type: "GROUP",
              maxTeams: parseInt(groupConfig.teamsPerGroup) || 4,
              timesToFaceEachOther: parseInt(groupConfig.timesToFaceEachOther) || 1,
              fixtureMode: groupConfig.fixtureMode || "ROUND_ROBIN",
              matchesPerTeam: groupConfig.fixtureMode === "FIXED_MATCHES" ? (parseInt(groupConfig.matchesPerTeam) || 1) : null,
            });
          }
          phasesPayload.push({ ...phaseData, blocks: groupBlocks });

          // Phase 2: Knockout stage
          let knockoutPhaseData;
          if (phase2Settings?.inheritSettings) {
            // Inherit pitches, points & timing from group phase, but use knockout's own schedule
            const knockoutSchedule = phase2Settings.matchSchedule || {};
            const today = new Date();
            const defaultStartDate = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
            const sixMonthsLater = new Date(today);
            sixMonthsLater.setMonth(sixMonthsLater.getMonth() + 6);
            const defaultEndDate = `${sixMonthsLater.getFullYear()}-${String(sixMonthsLater.getMonth() + 1).padStart(2, '0')}-${String(sixMonthsLater.getDate()).padStart(2, '0')}`;

            knockoutPhaseData = {
              ...phaseData,
              name: (phase2Settings.name || "Knockout stage").trim(),
              startAfterPreviousPhase: true,
              matchSchedule: {
                startDate: knockoutSchedule.startDate || defaultStartDate,
                endDate: knockoutSchedule.endDate || defaultEndDate,
                matchDays: knockoutSchedule.matchDays || ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
                matchTimings: knockoutSchedule.matchTimings || [],
                startTime: knockoutSchedule.startTime || "",
                dayEndTime: knockoutSchedule.dayEndTime || "21:00",
              },
            };
          } else {
            knockoutPhaseData = buildPhaseData(phase2Settings);
            knockoutPhaseData.startAfterPreviousPhase = true;
          }
          knockoutPhaseData.blocks = [{
            name: bracketConfig.bracketName || "Main Bracket",
            type: "BRACKET",
            maxTeams: resolveBracketTeams(),
            numberOfLegs: parseInt(bracketConfig.numberOfLegs) || 1,
          }];
          phasesPayload.push(knockoutPhaseData);
        }

        await dispatch(
          bulkCreateTournamentPhases({
            tournamentId,
            categoryId,
            payload: {
              templateType,
              phases: phasesPayload,
            },
          })
        ).unwrap();

        formatCreated({
          templateType,
          competitionId: tournamentId,
          categoryId,
          phaseCount: phasesPayload.length,
        });

        const templateNames = {
          knockoutPhase: "Knockout",
          groupPhase: "Group",
          groupStageKnockout: "Group + Knockout",
        };
        toast.success(`${templateNames[templateType]} format created successfully!`);
      }

      // Close modal on success and refresh content
      setIsPhaseSettingsModalOpen(false);
      setIsCreateFormatModalOpen(false);
      setEditingPhase(null);
      setPhaseInitialData(null);
      setSelectedTemplate("manualGrid");
      await refreshContent();
    } catch (error) {
      toast.error(error.message || "Failed to save stage");
    } finally {
      setIsLoading(false);
    }
  };

  // Opens the confirmation modal
  const handleDeletePhase = (phaseId) => {
    const phase = phases.find(p => p._id === phaseId || p.id === phaseId);
    setPhaseToDelete(phase || { id: phaseId, name: "this stage" });
    setIsPhaseDeleteModalOpen(true);
  };

  const confirmDeletePhase = async () => {
    if (!phaseToDelete) return;
    const phaseId = phaseToDelete.id || phaseToDelete._id;
    try {
      const tournamentId = selectedTournament?.id || selectedTournament?._id;
      const categoryId = selectedCategoryFromRedux?._id || selectedCategoryFromRedux?.id;

      if (!tournamentId || !categoryId) {
        toast.error("Please select a tournament category first");
        return;
      }

      setIsLoading(true);
      await dispatch(
        deletePhase({
          tournamentId,
          categoryId,
          phaseId,
        })
      ).unwrap();

      await refreshContent();
      setIsPhaseDeleteModalOpen(false);
      setPhaseToDelete(null);
      toast.success("Stage deleted successfully");
    } catch (error) {
      toast.error(error.message || "Error deleting stage");
    } finally {
      setIsLoading(false);
    }
  };

  // ============================================
  // MATCH HANDLERS
  // ============================================

  const openCreateMatch = (phaseGroup) => {
    setMatchMode("create");
    setSelectedPhaseForMatch(phaseGroup);
    setSelectedMatchGroup(null);
    setMatchFormData({ name: "", category: selectedCategory || "" });
    setIsMatchModalOpen(true);
  };

  const openEditMatch = (phaseGroup, matchGroup) => {
    setMatchMode("edit");
    setSelectedPhaseForMatch(phaseGroup);
    setSelectedMatchGroup(matchGroup);
    setMatchFormData({
      name: matchGroup.name || "",
      category: matchGroup.category || selectedCategory || "",
    });
    setIsMatchModalOpen(true);
  };

  const openDeleteMatch = (phaseGroup, matchGroup) => {
    setSelectedPhaseForMatch(phaseGroup);
    setSelectedMatchGroup(matchGroup);
    setIsDeleteMatchModalOpen(true);
  };

  const handleSubmitMatch = async (formData) => {
    setIsLoading(true);
    try {
      if (matchMode === "create") {
        const numMatches = parseInt(formData.numMatches) || 1;
        await dispatch(
          createTournamentBlock({
            tournamentId: selectedTournament.id,
            categoryId: selectedCategoryFromRedux._id,
            phaseId: selectedPhaseForMatch.id || selectedPhaseForMatch._id,
            blockData: {
              name: formData.name.trim() || "Match",
              type: "SINGLE_MATCH",
              maxTeams: 2,
              count: numMatches,
            },
          })
        ).unwrap();

        toast.success(numMatches > 1 ? `${numMatches} matches added` : "Match added successfully");
      } else {
        // Edit existing match
        await dispatch(
          updateBlock({
            tournamentId: selectedTournament.id,
            categoryId: selectedCategoryFromRedux._id,
            phaseId: selectedPhaseForMatch._id || selectedPhaseForMatch.id,
            blockId: selectedMatchGroup._id,
            blockData: {
              name: formData.name.trim(),
              type: "SINGLE_MATCH",
              maxTeams: 2,
            },
          })
        ).unwrap();

        toast.success("Match updated successfully");
      }

      setIsMatchModalOpen(false);
      setSelectedMatchGroup(null);
      setMatchFormData({ name: "", category: "" });
      await refreshContent();
    } catch (error) {
      toast.error(error.message || "Failed to save match");
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmDeleteMatch = async () => {
    setIsLoading(true);
    try {
      await dispatch(
        deleteBlock({
          tournamentId: selectedTournament.id,
          categoryId: selectedCategoryFromRedux._id,
          phaseId: selectedPhaseForMatch._id || selectedPhaseForMatch.id,
          blockId: selectedMatchGroup._id,
        })
      ).unwrap();

      toast.success("Match deleted successfully");
      setIsDeleteMatchModalOpen(false);
      setSelectedMatchGroup(null);
      await refreshContent();
    } catch (error) {
      toast.error(error.message || "Failed to delete match");
    } finally {
      setIsLoading(false);
    }
  };

  // ============================================
  // GROUP HANDLERS
  // ============================================

  const openCreateGroup = (phaseGroup) => {
    setGroupMode("create");
    setSelectedPhaseForGroup(phaseGroup);
    setSelectedGroup(null);
    setGroupFormData({
      name: "",
      numGroups: 1,
      teamsPerGroup: 4,
      category: selectedCategory || "",
      timesToFaceEachOther: 1
    });
    setIsGroupModalOpen(true);
  };

  const openEditGroup = (phaseGroup, group) => {
    setGroupMode("edit");
    setSelectedPhaseForGroup(phaseGroup);
    setSelectedGroup(group);
    setGroupFormData({
      name: group.name || "",
      numGroups: 1, // Edit always starts with 1 for existing groups
      teamsPerGroup: group.maxTeams || 4,
      category: selectedCategory || "",
      timesToFaceEachOther: group.timesToFaceEachOther || 1,
      fixtureMode: group.fixtureMode || "ROUND_ROBIN",
      matchesPerTeam: group.matchesPerTeam || 1,
    });
    setIsGroupModalOpen(true);
  };

  const openDeleteGroup = (phaseGroup, group) => {
    setSelectedPhaseForGroup(phaseGroup);
    setSelectedGroup(group);
    setIsDeleteGroupModalOpen(true);
  };

  const handleSubmitGroup = async (formData) => {
    setIsLoading(true);
    const loadingToast = toast.loading(formData.numGroups > 1 ? "Adding groups..." : "Adding group...");

    try {
      if (groupMode === "create") {
        const numGroups = parseInt(formData.numGroups) || 1;
        await dispatch(
          createTournamentBlock({
            tournamentId: selectedTournament.id,
            categoryId: selectedCategoryFromRedux._id,
            phaseId: selectedPhaseForGroup._id || selectedPhaseForGroup.id,
            blockData: {
              name: formData.name.trim(),
              type: "GROUP",
              maxTeams: parseInt(formData.teamsPerGroup),
              timesToFaceEachOther: parseInt(formData.timesToFaceEachOther) || 1,
              fixtureMode: formData.fixtureMode || "ROUND_ROBIN",
              matchesPerTeam: formData.fixtureMode === "FIXED_MATCHES" ? (parseInt(formData.matchesPerTeam) || 1) : null,
              count: numGroups,
            },
          })
        ).unwrap();

        toast.dismiss(loadingToast);
        toast.success(numGroups > 1 ? `${numGroups} groups added successfully` : "Group added successfully");
      } else {
        // Edit existing group
        await dispatch(
          updateBlock({
            tournamentId: selectedTournament.id,
            categoryId: selectedCategoryFromRedux._id,
            phaseId: selectedPhaseForGroup._id || selectedPhaseForGroup.id,
            blockId: selectedGroup._id,
            blockData: {
              name: formData.name.trim(),
              category: formData.category || selectedCategory,
              type: "GROUP",
              maxTeams: parseInt(formData.teamsPerGroup),
              timesToFaceEachOther: parseInt(formData.timesToFaceEachOther) || 1,
              fixtureMode: formData.fixtureMode || "ROUND_ROBIN",
              matchesPerTeam: formData.fixtureMode === "FIXED_MATCHES" ? (parseInt(formData.matchesPerTeam) || 1) : null,
            },
          })
        ).unwrap();

        toast.dismiss(loadingToast);
        toast.success("Group updated successfully");
      }

      setIsGroupModalOpen(false);
      setSelectedGroup(null);
      setGroupFormData({
        name: "",
        numGroups: 1,
        teamsPerGroup: 4,
        category: "",
        timesToFaceEachOther: 1
      });
      await refreshContent();
    } catch (error) {
      toast.dismiss(loadingToast);
      toast.error(error.message || "Failed to save group");
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmDeleteGroup = async () => {
    setIsLoading(true);
    const loadingToast = toast.loading("Deleting group...");

    try {
      await dispatch(
        deleteBlock({
          tournamentId: selectedTournament.id,
          categoryId: selectedCategoryFromRedux._id,
          phaseId: selectedPhaseForGroup._id || selectedPhaseForGroup.id,
          blockId: selectedGroup._id,
        })
      ).unwrap();

      toast.dismiss(loadingToast);
      toast.success("Group deleted successfully");
      setIsDeleteGroupModalOpen(false);
      setSelectedGroup(null);
      await refreshContent();
    } catch (error) {
      toast.dismiss(loadingToast);
      toast.error(error.message || "Failed to delete group");
    } finally {
      setIsLoading(false);
    }
  };

  // ============================================
  // BRACKET HANDLERS
  // ============================================

  const openCreateBracket = (phaseGroup) => {
    if (!phaseGroup) return;

    setBracketMode("create");
    setSelectedPhaseForBracket(phaseGroup);
    setSelectedBracket(null);
    setBracketFormData({
      name: "",
      teamsCount: 2,
      numberOfLegs: 1
    });
    setIsBracketModalOpen(true);
  };

  const openEditBracket = (phaseGroup, bracket) => {
    if (!phaseGroup || !bracket) return;

    setBracketMode("edit");
    setSelectedPhaseForBracket(phaseGroup);
    setSelectedBracket(bracket);
    setBracketFormData({
      name: bracket.name || "",
      teamsCount: bracket.maxTeams || bracket.teamsCount || 2,
      numberOfLegs: bracket.numberOfLegs || 1,
    });
    setIsBracketModalOpen(true);
  };

  const openDeleteBracket = (phaseGroup, bracket) => {
    if (!phaseGroup || !bracket) return;

    setSelectedPhaseForBracket(phaseGroup);
    setSelectedBracket(bracket);
    setIsDeleteBracketModalOpen(true);
  };

  const handleSubmitBracket = async (formData) => {
    setIsLoading(true);
    const isEditing = bracketMode === "edit" && selectedBracket;
    const loadingToast = toast.loading(isEditing ? "Updating bracket..." : "Adding bracket...");

    try {
      // Validate required data
      if (!selectedPhaseForBracket || !selectedTournament?.id || !selectedCategoryFromRedux?._id) {
        throw new Error("Missing required data");
      }

      const phaseId = selectedPhaseForBracket._id || selectedPhaseForBracket.id;
      if (!phaseId) {
        throw new Error("Invalid phase ID");
      }

      if (isEditing) {
        const blockId = selectedBracket._id || selectedBracket.id;
        await dispatch(
          updateBlock({
            tournamentId: selectedTournament.id,
            categoryId: selectedCategoryFromRedux._id,
            phaseId,
            blockId,
            blockData: {
              name: formData.name.trim() || "New Bracket",
              type: "BRACKET",
              maxTeams: parseInt(formData.teamsCount) || 2,
              numberOfLegs: parseInt(formData.numberOfLegs) || 1,
            },
          })
        ).unwrap();
      } else {
        await dispatch(
          createTournamentBlock({
            tournamentId: selectedTournament.id,
            categoryId: selectedCategoryFromRedux._id,
            phaseId,
            blockData: {
              name: formData.name.trim() || "New Bracket",
              type: "BRACKET",
              maxTeams: parseInt(formData.teamsCount) || 2,
              numberOfLegs: parseInt(formData.numberOfLegs) || 1,
            },
          })
        ).unwrap();
      }

      toast.dismiss(loadingToast);
      toast.success(isEditing ? "Bracket updated successfully" : "Bracket added successfully");
      setIsBracketModalOpen(false);
      setSelectedBracket(null);
      setBracketFormData({
        name: "",
        teamsCount: 2,
        numberOfLegs: 1
      });
      await refreshContent();
    } catch (error) {
      toast.dismiss(loadingToast);
      toast.error(error.message || "Failed to save bracket");
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmDeleteBracket = async () => {
    setIsLoading(true);
    const loadingToast = toast.loading("Deleting bracket...");

    try {
      if (!selectedPhaseForBracket || !selectedTournament?.id || !selectedCategoryFromRedux?._id) {
        throw new Error("Missing required data to delete bracket");
      }

      const phaseId = selectedPhaseForBracket._id || selectedPhaseForBracket.id;
      if (!phaseId) {
        throw new Error("Invalid phase ID");
      }

      const bracketBlockId = selectedBracket?._id;
      if (!bracketBlockId) {
        throw new Error("Invalid bracket block ID");
      }

      // Delete the BRACKET block from the phase
      await dispatch(
        deleteBlock({
          tournamentId: selectedTournament.id,
          categoryId: selectedCategoryFromRedux._id,
          phaseId,
          blockId: bracketBlockId,
        })
      ).unwrap();

      toast.dismiss(loadingToast);
      toast.success("Bracket removed successfully");
      setIsDeleteBracketModalOpen(false);
      setSelectedBracket(null);
      await refreshContent();
    } catch (error) {
      toast.dismiss(loadingToast);
      toast.error(error.message || "Failed to delete bracket");
    } finally {
      setIsLoading(false);
    }
  };

  // ============================================
  // SECTION HANDLERS
  // ============================================

  const openCreateSection = (phaseGroup) => {
    if (!phaseGroup) return;
    const bracketBlock = phaseGroup.groups?.find(b => b.type === "BRACKET");
    if (!bracketBlock) return;

    setSelectedBracketForSection({
      phaseGroup: phaseGroup,
      bracketName: bracketBlock.name
    });
    setSectionFormData({ name: "" });
    setIsBracketSectionModalOpen(true);
  };

  const handleSubmitSection = async (formData) => {
    setIsLoading(true);
    const loadingToast = toast.loading("Adding section...");

    try {
      if (!selectedBracketForSection || !selectedBracketForSection.phaseGroup) {
        throw new Error("No bracket selected for this section");
      }

      const bracketName = selectedBracketForSection.bracketName;

      // Try to copy timing / pitch settings from an existing phase
      // 1) Prefer a phase inside this stageGroup (same name grouping)
      // 2) Fallback to the last phase in the selectedCategoryFromRedux
      const phaseGroup = selectedBracketForSection.phaseGroup;
      const sourcePhaseFromGroup = phaseGroup.brackets && phaseGroup.brackets.length > 0
        ? phaseGroup.brackets[0]
        : null;

      const sourcePhaseFromSeason =
        selectedCategoryFromRedux?.phases && selectedCategoryFromRedux.phases.length > 0
          ? selectedCategoryFromRedux.phases[selectedCategoryFromRedux.phases.length - 1]
          : null;

      const sourcePhase = sourcePhaseFromGroup || sourcePhaseFromSeason;

      // Build phaseData based on a valid existing phase so backend validations pass
      const phaseData = {
        name: formData.name.trim(),
        followMatchTimings: sourcePhase?.followMatchTimings ?? true,
        availablePitches: sourcePhase?.availablePitches && sourcePhase.availablePitches.length > 0
          ? sourcePhase.availablePitches
          : selectedTournament?.matchVenue?.pitches || [],
        startAfterPreviousPhase: false,
        matchRules: sourcePhase?.matchRules || {
          gameDuration: "30",
          breaks: "1",
          breakDuration: "5",
          intervalBetweenMatches: "5",
          pointsForWin: "3",
          pointsForDraw: "1",
          pointsForLoss: "0",
        },
        // Only include matchSchedule if we have a valid source with dates
        ...(sourcePhase?.matchSchedule?.startDate && {
          matchSchedule: {
            startDate: sourcePhase.matchSchedule.startDate,
            endDate:
              sourcePhase.matchSchedule.endDate || sourcePhase.matchSchedule.startDate,
            matchDays: sourcePhase.matchSchedule.matchDays || [],
            matchTimings: sourcePhase.matchSchedule.matchTimings || [],
            startTime: sourcePhase.matchSchedule.startTime || "",
            dayEndTime: sourcePhase.matchSchedule.dayEndTime || "",
          },
        }),
      };

      await dispatch(
        createTournamentPhase({
          tournamentId: selectedTournament.id,
          categoryId: selectedCategoryFromRedux._id,
          phaseData,
        })
      ).unwrap();

      toast.dismiss(loadingToast);
      toast.success("Section added successfully");
      setIsBracketSectionModalOpen(false);
      setSelectedBracketForSection(null);
      setSectionFormData({ name: "" });
      await refreshContent();
    } catch (error) {
      toast.dismiss(loadingToast);
      toast.error(error.message || "Failed to save section");
    } finally {
      setIsLoading(false);
    }
  };

  // ============================================
  // BRACKET GROUP HANDLERS
  // ============================================

  const openCreateBracketGroup = (phaseGroup) => {
    if (!phaseGroup) return;
    const bracketBlock = phaseGroup.groups?.find(b => b.type === "BRACKET");
    if (!bracketBlock) return;

    setSelectedBracketForGroup({
      phaseGroup: phaseGroup,
      bracketName: bracketBlock.name
    });
    setBracketGroupFormData({
      name: "",
      numGroups: 1,
      teamsPerGroup: 4
    });
    setIsBracketGroupModalOpen(true);
  };

  const handleSubmitBracketGroup = async (formData) => {
    setIsLoading(true);
    const loadingToast = toast.loading(formData.numGroups > 1 ? "Adding groups..." : "Adding group...");

    try {
      if (!selectedBracketForGroup || !selectedBracketForGroup.phaseGroup) {
        throw new Error("No bracket selected for this group");
      }

      const phaseId = selectedBracketForGroup.phaseGroup.id || selectedBracketForGroup.phaseGroup._id;

      // Logic for creating multiple groups
      if (formData.numGroups > 1) {
        for (let i = 1; i <= formData.numGroups; i++) {
          const newGroupName = `${formData.name}${i}`;

          await dispatch(
            createTournamentBlock({
              tournamentId: selectedTournament.id,
              categoryId: selectedCategoryFromRedux._id,
              phaseId: phaseId,
              blockData: {
                name: newGroupName,
                type: "GROUP",
                maxTeams: parseInt(formData.teamsPerGroup),
                timesToFaceEachOther: 1, // Default value
              },
            })
          ).unwrap();
        }

        toast.dismiss(loadingToast);
        toast.success(`${formData.numGroups} groups added successfully`);
      } else {
        // Create single group
        await dispatch(
          createTournamentBlock({
            tournamentId: selectedTournament.id,
            categoryId: selectedCategoryFromRedux._id,
            phaseId: phaseId,
            blockData: {
              name: formData.name.trim(),
              type: "GROUP",
              maxTeams: parseInt(formData.teamsPerGroup),
              timesToFaceEachOther: 1, // Default value
            },
          })
        ).unwrap();

        toast.dismiss(loadingToast);
        toast.success("Group added successfully");
      }

      setIsBracketGroupModalOpen(false);
      setSelectedBracketForGroup(null);
      setBracketGroupFormData({
        name: "",
        numGroups: 1,
        teamsPerGroup: 4
      });
      await refreshContent();
    } catch (error) {
      toast.dismiss(loadingToast);
      toast.error(error.message || "Failed to save group");
    } finally {
      setIsLoading(false);
    }
  };

  // ============================================
  // BRACKET MATCH HANDLERS
  // ============================================

  const openCreateBracketMatch = (phaseGroup) => {
    if (!phaseGroup) return;
    const bracketBlock = phaseGroup.groups?.find(b => b.type === "BRACKET");
    if (!bracketBlock) return;

    setSelectedBracketForMatch({
      phaseGroup: phaseGroup,
      bracketName: bracketBlock.name
    });
    setBracketMatchFormData({ name: "" });
    setIsBracketMatchModalOpen(true);
  };

  const handleSubmitBracketMatch = async (formData) => {
    setIsLoading(true);
    const loadingToast = toast.loading("Adding match...");

    try {
      if (!selectedBracketForMatch || !selectedBracketForMatch.phaseGroup) {
        throw new Error("No bracket selected for this match");
      }

      const phaseId = selectedBracketForMatch.phaseGroup.id || selectedBracketForMatch.phaseGroup._id;

      // Create a single match (KNOCKOUT type with maxTeams=2)
      await dispatch(
        createTournamentBlock({
          tournamentId: selectedTournament.id,
          categoryId: selectedCategoryFromRedux._id,
          phaseId: phaseId,
          blockData: {
            name: formData.name.trim(),
            type: "SINGLE_MATCH",
            maxTeams: 2,
            timesToFaceEachOther: 1,
          },
        })
      ).unwrap();

      toast.dismiss(loadingToast);
      toast.success("Match added successfully");
      setIsBracketMatchModalOpen(false);
      setSelectedBracketForMatch(null);
      setBracketMatchFormData({ name: "" });
      await refreshContent();
    } catch (error) {
      toast.dismiss(loadingToast);
      toast.error(error.message || "Failed to save match");
    } finally {
      setIsLoading(false);
    }
  };

  // Toggle phase lock/unlock
  const handleTogglePhaseLock = async (phaseId) => {
    const tournamentId = selectedTournament?.id || selectedTournament?._id;
    const categoryId = selectedCategoryFromRedux?._id || selectedCategoryFromRedux?.id;
    if (!tournamentId || !categoryId) return;

    const phase = (selectedCategoryFromRedux?.phases || []).find(
      (p) => (p._id || p.id) === phaseId
    );
    if (!phase) return;

    const newLocked = !phase.locked;
    setIsLoading(true);
    const loadingToast = toast.loading(newLocked ? "Locking stage..." : "Unlocking stage...");

    try {
      await dispatch(
        updatePhase({
          tournamentId,
          categoryId,
          phaseId,
          phaseData: { locked: newLocked },
        })
      ).unwrap();

      await refreshContent();
      toast.dismiss(loadingToast);
      toast.success(newLocked ? "Stage locked" : "Stage unlocked");
    } catch (error) {
      toast.dismiss(loadingToast);
      toast.error(error?.message || "Failed to update stage lock");
    } finally {
      setIsLoading(false);
    }
  };

  // Inline rename a block (group, bracket, match)
  const handleRenameBlock = async (phaseId, blockId, newName) => {
    const tournamentId = selectedTournament?.id || selectedTournament?._id;
    const categoryId = selectedCategoryFromRedux?._id || selectedCategoryFromRedux?.id;
    if (!tournamentId || !categoryId || !newName.trim()) return;

    try {
      await dispatch(
        updateBlock({ tournamentId, categoryId, phaseId, blockId, blockData: { name: newName.trim() } })
      ).unwrap();
      await refreshContent();
    } catch (error) {
      toast.error(error?.message || "Failed to rename block");
    }
  };

  // Inline rename a phase
  const handleRenamePhase = async (phaseId, newName) => {
    const tournamentId = selectedTournament?.id || selectedTournament?._id;
    const categoryId = selectedCategoryFromRedux?._id || selectedCategoryFromRedux?.id;
    if (!tournamentId || !categoryId || !newName.trim()) return;

    try {
      await dispatch(
        updatePhase({ tournamentId, categoryId, phaseId, phaseData: { name: newName.trim() } })
      ).unwrap();
      await refreshContent();
    } catch (error) {
      toast.error(error?.message || "Failed to rename stage");
    }
  };

  // Immediately create a phase with default values (no modal)
  const handleAddPhaseQuick = async () => {
    const tournamentId = selectedTournament?.id || selectedTournament?._id;
    const categoryId = selectedCategoryFromRedux?._id || selectedCategoryFromRedux?.id;
    if (!tournamentId || !categoryId) {
      toast.error("Please select a tournament and category first");
      return;
    }

    // Determine next phase number
    const existingPhases = selectedCategoryFromRedux?.phases || [];
    const phaseName = `Stage ${existingPhases.length + 1}`;

    // Get all pitches from tournament
    const pitches = selectedTournament?.matchVenue?.pitches;
    const numberOfPitches = selectedTournament?.matchVenue?.numberOfPitches || 0;
    let availablePitches = [];
    if (pitches?.length) {
      availablePitches = pitches.map((p) => p.name || p);
    } else if (numberOfPitches > 0) {
      availablePitches = Array.from({ length: numberOfPitches }, (_, i) => `Pitch ${String.fromCharCode(65 + i)}`);
    }

    const today = new Date();
    const startDate = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
    const sixMonthsLater = new Date(today);
    sixMonthsLater.setMonth(sixMonthsLater.getMonth() + 6);
    const endDate = `${sixMonthsLater.getFullYear()}-${String(sixMonthsLater.getMonth() + 1).padStart(2, "0")}-${String(sixMonthsLater.getDate()).padStart(2, "0")}`;

    const phaseData = {
      name: phaseName,
      followMatchTimings: false,
      availablePitches,
      startAfterPreviousPhase: false,
      matchRules: {
        gameDuration: 30,
        breaks: 2,
        breakDuration: 5,
        intervalBetweenMatches: 5,
        pointsForWin: 3,
        pointsForDraw: 1,
        pointsForLoss: 0,
      },
      matchSchedule: {
        startDate,
        endDate,
        matchDays: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
        selectedDates: [],
        matchTimings: [],
        startTime: "09:00",
        dayEndTime: "21:00",
      },
    };

    setIsLoading(true);
    try {
      await dispatch(
        createTournamentPhase({ tournamentId, categoryId, phaseData })
      ).unwrap();
      toast.success("Stage added");
      await refreshContent();
    } catch (error) {
      toast.error(error?.message || "Failed to add stage");
    } finally {
      setIsLoading(false);
    }
  };

  return {
    // Core
    refreshContent,
    handleTemplateSelect,

    // Team assignment
    getAvailableTeams,
    getTeamById,
    handleAssignTeam,
    handleUnassignTeam,
    handleAutoAssignTeams,
    handleAutoAssignAllGroups,
    openTeamSelector,
    closeTeamSelector,

    // Progression rules
    handleCreateRule,
    handleUpdateRule,
    handleDeleteRule,
    handleExecuteRules,
    openEditRule,
    getRuleSources,
    handleAssignRuleFromClick,
    getProgressionRuleText,
    findRuleForSlot,
    handleRemoveRule,
    handleCreateRuleFromDrag,

    // Extra points
    openExtraPointsModal,
    handleUpdateExtraPoints,

    // Rename
    handleRenameBlock,
    handleRenamePhase,

    // Phase
    handleAddPhaseQuick,
    handleSubmitPhase,
    handleDeletePhase,
    confirmDeletePhase,
    handleTogglePhaseLock,

    // Match
    openCreateMatch,
    openEditMatch,
    openDeleteMatch,
    handleSubmitMatch,
    handleConfirmDeleteMatch,

    // Group
    openCreateGroup,
    openEditGroup,
    openDeleteGroup,
    handleSubmitGroup,
    handleConfirmDeleteGroup,

    // Bracket
    openCreateBracket,
    openEditBracket,
    openDeleteBracket,
    handleSubmitBracket,
    handleConfirmDeleteBracket,

    // Section
    openCreateSection,
    handleSubmitSection,

    // Bracket group
    openCreateBracketGroup,
    handleSubmitBracketGroup,

    // Bracket match
    openCreateBracketMatch,
    handleSubmitBracketMatch,
  };
};

export default useFormatHandlers;
